import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, imgSrc } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";

const INPUT_CLASS =
  "w-full border border-white/10 bg-[#0d0d0d] px-4 py-3 text-sm font-light text-[#F5F5F5] placeholder:text-[#5a5a5a] transition-colors duration-300 focus:border-gold focus:outline-none rounded-none";

const EMPTY = { judul: "", ringkasan: "", link: "", gambar: "", file: null, preview: null };

export default function AdminBerita() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/berita").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY }); };
  const openEdit = (b) => {
    setEditId(b.id);
    setForm({
      judul: b.judul, ringkasan: b.ringkasan || "", link: b.link || "",
      gambar: b.gambar || "", file: null, preview: null,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let gambar = form.gambar || null;
      if (form.file) {
        const fd = new FormData();
        fd.append("file", form.file);
        const up = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        gambar = up.data.url;
      }
      const payload = {
        judul: form.judul,
        ringkasan: form.ringkasan || null,
        link: form.link || null,
        gambar,
      };
      if (editId) {
        await api.put(`/berita/${editId}`, payload);
        toast.success("Berita diperbarui.");
      } else {
        await api.post("/berita", payload);
        toast.success("Berita baru diterbitkan.");
      }
      setForm(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ylbh_admin_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Gagal menyimpan berita.");
      }
    } finally {
      setSaving(false);
    }
  };

  const hapus = async (b) => {
    if (!window.confirm(`Hapus berita "${b.judul}"? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/berita/${b.id}`);
      toast.success("Berita dihapus.");
      load();
    } catch {
      toast.error("Gagal menghapus berita.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AdminShell title="Kelola Berita">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl text-[#F5F5F5]">Berita Hukum</h1>
          <p className="mt-2 text-sm font-light text-[#A3A3A3]">
            Berita yang Anda tulis di sini tampil paling atas di halaman Berita Hukum, di atas himpunan berita otomatis.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="berita-baru-button"
          className="flex items-center gap-2 bg-gold px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:bg-gold-light"
        >
          <Plus size={15} /> Tulis Berita
        </button>
      </div>

      {form && (
        <form
          data-testid="berita-form"
          onSubmit={submit}
          className="mt-8 space-y-5 border border-gold/30 bg-[#0A0A0A] p-6 lg:p-8"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl text-[#F5F5F5]">
              {editId ? "Ubah Berita" : "Berita Baru"}
            </h2>
            <button type="button" onClick={() => setForm(null)} aria-label="Tutup formulir" data-testid="berita-form-close">
              <X size={20} className="text-[#737373] hover:text-white" />
            </button>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Judul Berita *</label>
              <input data-testid="berita-judul-input" required minLength={5} value={form.judul} onChange={set("judul")} className={INPUT_CLASS} placeholder="Judul berita" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Ringkasan</label>
              <textarea data-testid="berita-ringkasan-textarea" rows={2} value={form.ringkasan} onChange={set("ringkasan")} className={`${INPUT_CLASS} resize-y`} placeholder="Ringkasan singkat berita" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Tautan Sumber (opsional)</label>
              <input data-testid="berita-link-input" type="url" value={form.link} onChange={set("link")} className={INPUT_CLASS} placeholder="https://… (tautan ke berita asli)" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">
                Gambar — unggah dari komputer (opsional, maks 5 MB)
              </label>
              <div className="flex flex-wrap items-center gap-5">
                <label
                  data-testid="berita-upload-label"
                  className="flex cursor-pointer items-center gap-2 border border-dashed border-gold/50 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-ink"
                >
                  <ImagePlus size={15} /> Pilih Gambar
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    data-testid="berita-file-input"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setForm((prev) => ({ ...prev, file: f, preview: URL.createObjectURL(f) }));
                    }}
                  />
                </label>
                {(form.preview || form.gambar) && (
                  <img
                    src={form.preview || imgSrc(form.gambar)}
                    alt="Pratinjau gambar berita"
                    data-testid="berita-preview-image"
                    className="h-20 w-32 border border-white/10 object-cover"
                  />
                )}
              </div>
            </div>
          </div>
          <button
            type="submit"
            data-testid="berita-simpan-button"
            disabled={saving}
            className="bg-gold px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:bg-gold-light disabled:opacity-60"
          >
            {saving ? "Menyimpan…" : editId ? "Simpan Perubahan" : "Terbitkan"}
          </button>
        </form>
      )}

      <div className="mt-10 space-y-3">
        {items === null ? (
          <p className="py-16 text-center font-serif text-xl italic text-[#737373]">Memuat…</p>
        ) : items.length === 0 ? (
          <p data-testid="berita-admin-empty" className="border border-white/10 bg-[#0d0d0d] py-16 text-center font-serif text-xl italic text-[#737373]">
            Belum ada berita buatan admin. Klik "Tulis Berita" untuk memulai.
          </p>
        ) : (
          items.map((b) => (
            <div
              key={b.id}
              data-testid={`berita-row-${b.id}`}
              className="flex flex-wrap items-center gap-x-5 gap-y-3 border border-white/10 bg-[#0d0d0d] px-5 py-4"
            >
              {b.gambar && (
                <img src={imgSrc(b.gambar)} alt={b.judul} className="h-12 w-16 border border-white/10 object-cover grayscale-[30%]" />
              )}
              <span className="font-serif text-lg text-[#F5F5F5]">{b.judul}</span>
              <span className="text-xs text-[#737373]">{b.tanggal}</span>
              <span className="ml-auto flex gap-2">
                <button
                  onClick={() => openEdit(b)}
                  data-testid={`berita-edit-${b.id}`}
                  className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
                >
                  <Pencil size={12} /> Ubah
                </button>
                <button
                  onClick={() => hapus(b)}
                  data-testid={`berita-hapus-${b.id}`}
                  className="flex items-center gap-1.5 border border-red-500/30 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-red-300/80 transition-colors hover:border-red-400 hover:text-red-300"
                >
                  <Trash2 size={12} /> Hapus
                </button>
              </span>
            </div>
          ))
        )}
      </div>
    </AdminShell>
  );
}
